/**
 * Which QMS folder this record is filed in, and the control that files, moves or takes it out.
 *
 * ONE FOLDER EACH (Ed, 2026-11-03), so filing into a second folder MOVES the record, and the dialog says which folder
 * it is leaving -- the same courtesy ProjectFiling, beside it, pays for projects.
 *
 * FILING IS SHARING. Everyone a folder is given to can see what is filed in it, so the server lets only somebody holding
 * the record's edit privilege file it (MayFile), and only into a folder where they hold File. The control is absent
 * rather than present and refusing for anybody else; the server is still the boundary.
 *
 * A RECORD FILED WHERE THIS PERSONA CANNOT SEE says "Filed in a folder you cannot see", never the folder's name.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {hasPrivilege} from "../../util/SessionContext";
import {Blue3, Grey5, Grey7, Grey17} from "../../theme/styles";

type Row = Record<string, any>;

/** "Clinical / Site 104 / Contracts" for each folder the caller may file into, sorted by path. */
export function fileableFolders(folders: Row[]): {USID: string; Path: string}[] {
    const byId = new Map(folders.map(one => [one.USID, one]));
    const pathOf = (one: Row) => {
        const names: string[] = [];
        let current: Row | undefined = one;
        const seen = new Set<string>();
        while (current && !seen.has(current.USID)) {
            seen.add(current.USID);
            names.unshift(current.Name);
            current = current.ParentFolderUSID ? byId.get(current.ParentFolderUSID) : undefined;
        }
        return names.join(" / ");
    };
    return folders.filter(one => one.Level === "FILE" || one.Level === "MANAGE")
        .map(one => ({USID: one.USID, Path: pathOf(one)}))
        .sort((a, b) => a.Path.localeCompare(b.Path));
}

export default function RecordFolderField({revisionUSID}: {revisionUSID?: string | null}) {
    const appContext: any = useContext(AppContext);
    const api = useMemo(() => makeCresAxios(appContext?.sessionContext),
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                        [appContext?.sessionContext]);
    const navigate = useNavigate();

    const [state, setState] = useState<Row | null>(null);
    const [choosing, setChoosing] = useState(false);
    const [choices, setChoices] = useState<{USID: string; Path: string}[]>([]);
    const [chosen, setChosen] = useState("");
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!revisionUSID || !hasPrivilege("folder:view")) return;
        api.get("t1/v1/folder_records", {params: {ObjectRevisionUSID: revisionUSID}})
            .then((answer: any) => setState(answer.data))
            // A READ THAT WILL NOT ANSWER LEAVES THE LINE OFF rather than claiming "not in a folder".
            .catch(() => setState(null));
    }, [api, revisionUSID]);

    useEffect(() => { load(); }, [load]);

    const openChooser = () => {
        setRefusal(null); setChosen(""); setChoosing(true);
        api.get("t1/v1/folders")
            .then((answer: any) => setChoices(fileableFolders(answer?.data?.Folders ?? [])
                .filter(one => one.USID !== state?.Folder?.USID)))
            .catch(() => setChoices([]));
    };

    const act = (request: Promise<any>, after: () => void) => {
        setBusy(true); setRefusal(null);
        request.then(() => { after(); load(); })
            .catch((problem: any) => setRefusal(problem?.response?.data?.Cause ?? "That did not work, and the reason did not come back."))
            .finally(() => setBusy(false));
    };

    if (!state) return null;
    const folder = state.Folder;

    return <Box sx={{display: "flex", flexDirection: "column", gap: "1px", minWidth: 220}}>
        <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>Folder</Typography>
        <Box sx={{display: "flex", alignItems: "center", gap: "10px", minHeight: "21px", flexWrap: "wrap"}}>
            {folder ? (
                <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}} onClick={() => navigate(`/folders/${folder.USID}`)}>
                    {folder.Name}
                </Typography>
            ) : (
                <Typography sx={{fontSize: 13, color: Grey17}}>
                    {state.Filed ? "Filed in a folder you cannot see" : "Not in a folder"}
                </Typography>
            )}
            {state.MayFile ? (
                <Typography sx={{fontSize: 12, color: Blue3, cursor: "pointer"}} onClick={openChooser}>
                    {state.Filed ? "Move" : "File"}
                </Typography>
            ) : null}
            {state.MayFile && state.Filed ? (
                <Typography sx={{fontSize: 12, color: Grey7, cursor: "pointer"}}
                            onClick={() => act(api.delete("t1/v1/folder_records", {params: {ObjectRevisionUSID: revisionUSID}}), () => {})}>
                    Remove
                </Typography>
            ) : null}
        </Box>
        {refusal ? <Typography sx={{fontSize: 11.5, color: "#B3261E", lineHeight: 1.5, pt: "4px", maxWidth: 320}}>{refusal}</Typography> : null}

        <Dialog open={choosing} onClose={() => setChoosing(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>{state.Filed ? "Move this record to another folder" : "File this record in a folder"}</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, pt: "8px !important"}}>
                {state.Filed ? (
                    <Typography sx={{fontSize: 13, color: Grey7}}>
                        A record lives in one folder, so this takes it out of {folder ? `“${folder.Name}”` : "the folder it is in"}.
                        The people that folder is given to stop seeing it unless something else lets them.
                    </Typography>
                ) : null}
                <Typography sx={{fontSize: 13, color: Grey7}}>
                    Everyone the folder is given to will be able to see this record, read-only.
                </Typography>
                <TextField select size="small" label="Folder" value={chosen} onChange={event => setChosen(event.target.value)}>
                    {choices.map(one => <MenuItem key={one.USID} value={one.USID}>{one.Path}</MenuItem>)}
                </TextField>
                {!choices.length ? (
                    <Typography sx={{fontSize: 12.5, color: Grey5}}>There is no folder you may file into. A folder&rsquo;s File or Manage grant lets you.</Typography>
                ) : null}
                {refusal ? <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{refusal}</Typography> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setChoosing(false)} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disabled={busy || !chosen} sx={{textTransform: "none"}}
                        onClick={() => act(api.post("t1/v1/folder_records", {FolderUSID: chosen, ObjectRevisionUSID: revisionUSID}),
                                           () => setChoosing(false))}>
                    {state.Filed ? "Move" : "File"}
                </Button>
            </DialogActions>
        </Dialog>
    </Box>;
}
